import { useState, useEffect } from "react";
import confetti from "canvas-confetti";
import { Award, Sparkles, Gift, Wallet } from "lucide-react";

/**
 * CreditsEarnedAnimation - Visual celebration when users earn credits/points
 * 
 * Usage:
 * <CreditsEarnedAnimation
 *   show={showAnimation}
 *   type="rewards" // "rewards" | "service" | "referral" | "gift"
 *   amount={100}
 *   message="You earned 100 rewards points!"
 *   onComplete={() => setShowAnimation(false)}
 * />
 */
const CreditsEarnedAnimation = ({ 
  show, 
  type = "rewards", 
  amount = 0, 
  message = "", 
  onComplete 
}) => {
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (show && !animating) {
      setVisible(true);
      setAnimating(true);
      
      // Fire confetti
      fireConfetti(type);
      
      // Auto-hide after 3 seconds
      const timer = setTimeout(() => {
        setVisible(false);
        setAnimating(false);
        onComplete?.();
      }, 3500);
      
      return () => clearTimeout(timer);
    }
  }, [show]);

  const fireConfetti = (creditType) => {
    const colors = {
      rewards: ["#9333ea", "#a855f7", "#c084fc", "#7c3aed"], // Purple
      service: ["#10b981", "#34d399", "#6ee7b7", "#059669"], // Green/Emerald
      referral: ["#f97316", "#fb923c", "#fdba74", "#ea580c"], // Orange
      gift: ["#ec4899", "#f472b6", "#f9a8d4", "#db2777"], // Pink
    };

    const selectedColors = colors[creditType] || colors.rewards;

    // First burst - center
    confetti({
      particleCount: 80,
      spread: 70,
      origin: { y: 0.6, x: 0.5 },
      colors: selectedColors,
      startVelocity: 30,
      gravity: 0.8,
    });

    // Second burst - left
    setTimeout(() => {
      confetti({
        particleCount: 40,
        angle: 60,
        spread: 55,
        origin: { x: 0.3, y: 0.65 },
        colors: selectedColors,
        startVelocity: 25,
      });
    }, 150);

    // Third burst - right
    setTimeout(() => {
      confetti({
        particleCount: 40,
        angle: 120,
        spread: 55,
        origin: { x: 0.7, y: 0.65 },
        colors: selectedColors,
        startVelocity: 25,
      });
    }, 300);
  };

  const getIcon = () => {
    switch (type) {
      case "service":
        return <Wallet className="w-12 h-12" />;
      case "referral":
        return <Gift className="w-12 h-12" />;
      case "gift":
        return <Sparkles className="w-12 h-12" />;
      default:
        return <Award className="w-12 h-12" />;
    }
  };

  const getColors = () => {
    switch (type) {
      case "service":
        return {
          bg: "from-emerald-500 via-emerald-600 to-teal-700",
          glow: "shadow-emerald-500/50",
          text: "text-emerald-100",
          ring: "ring-emerald-400",
        };
      case "referral":
        return {
          bg: "from-orange-500 via-orange-600 to-amber-700",
          glow: "shadow-orange-500/50",
          text: "text-orange-100",
          ring: "ring-orange-400",
        };
      case "gift":
        return {
          bg: "from-pink-500 via-pink-600 to-rose-700",
          glow: "shadow-pink-500/50",
          text: "text-pink-100",
          ring: "ring-pink-400",
        };
      default: // rewards
        return {
          bg: "from-purple-500 via-purple-600 to-indigo-700",
          glow: "shadow-purple-500/50",
          text: "text-purple-100",
          ring: "ring-purple-400",
        };
    }
  };

  const getTitle = () => {
    switch (type) {
      case "service":
        return "Service Credits Earned!";
      case "referral":
        return "Referral Bonus!";
      case "gift":
        return "Gift Card Applied!";
      default:
        return "Rewards Points Earned!";
    }
  };

  if (!visible) return null;

  const colors = getColors();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm animate-fade-in"
        style={{ animation: "fadeIn 0.3s ease-out" }}
      />
      
      {/* Main card */}
      <div
        className={`relative bg-gradient-to-br ${colors.bg} rounded-3xl p-8 shadow-2xl ${colors.glow} ring-4 ${colors.ring} ring-opacity-50 transform animate-bounce-in`}
        style={{ 
          animation: "bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)",
          maxWidth: "90vw",
          width: "380px"
        }}
      >
        {/* Glow effect */}
        <div 
          className="absolute inset-0 rounded-3xl opacity-50 blur-xl"
          style={{
            background: `radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, transparent 70%)`,
          }}
        />
        
        {/* Sparkle decorations */}
        <div className="absolute -top-3 -left-3 animate-pulse">
          <Sparkles className="w-8 h-8 text-yellow-300" />
        </div>
        <div className="absolute -top-2 -right-4 animate-pulse" style={{ animationDelay: "0.3s" }}>
          <Sparkles className="w-6 h-6 text-yellow-300" />
        </div>
        <div className="absolute -bottom-2 -right-3 animate-pulse" style={{ animationDelay: "0.6s" }}>
          <Sparkles className="w-7 h-7 text-yellow-300" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 text-center text-white">
          {/* Icon with glow */}
          <div className="flex justify-center mb-4">
            <div 
              className="p-4 bg-white/20 rounded-full backdrop-blur-sm animate-pulse-slow"
              style={{ 
                boxShadow: "0 0 40px rgba(255,255,255,0.4)",
                animation: "pulse 2s ease-in-out infinite"
              }}
            >
              {getIcon()}
            </div>
          </div>
          
          {/* Title */}
          <h2 
            className="text-2xl font-bold mb-2 animate-slide-up"
            style={{ animation: "slideUp 0.4s ease-out 0.2s both" }}
          >
            {getTitle()}
          </h2>
          
          {/* Amount with counter effect */}
          <div 
            className="text-5xl font-black mb-3 tracking-tight animate-scale-in"
            style={{ 
              animation: "scaleIn 0.5s ease-out 0.3s both",
              textShadow: "0 4px 20px rgba(0,0,0,0.3)"
            }}
          >
            +{amount.toLocaleString()}
          </div>
          
          {/* Message */}
          {message && (
            <p 
              className={`${colors.text} text-sm animate-fade-in-up`}
              style={{ animation: "fadeInUp 0.4s ease-out 0.5s both" }}
            >
              {message}
            </p>
          )}
          
          {/* Subtle instruction */}
          <p 
            className="text-white/60 text-xs mt-4 animate-fade-in"
            style={{ animation: "fadeIn 0.3s ease-out 1s both" }}
          >
            This notification will close automatically
          </p>
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); }
          70% { transform: scale(0.95); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.5); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes fadeInUp {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(255,255,255,0.4); }
          50% { box-shadow: 0 0 40px rgba(255,255,255,0.6); }
        }
      `}</style>
    </div>
  );
};

export default CreditsEarnedAnimation;
